(() => {
  const ROOT_SELECTOR = '[data-mjs-dds-root]';
  const rootCleanups = new WeakMap();
  const SECOND_MS = 1_000;
  const MINUTE_MS = 60 * SECOND_MS;
  const HOUR_MS = 60 * MINUTE_MS;
  const DAY_MS = 24 * HOUR_MS;
  // Jay-confirmed calendar through 3 October; Monday to Saturday, fixed Brisbane time.
  const RELEASE_DATES = Object.freeze([
    '2026-09-07',
    '2026-09-08',
    '2026-09-09',
    '2026-09-10',
    '2026-09-11',
    '2026-09-12',
    '2026-09-14',
    '2026-09-15',
    '2026-09-16',
    '2026-09-17',
    '2026-09-18',
    '2026-09-19',
    '2026-09-21',
    '2026-09-22',
    '2026-09-23',
    '2026-09-24',
    '2026-09-25',
    '2026-09-26',
    '2026-09-28',
    '2026-09-29',
    '2026-09-30',
    '2026-10-01',
    '2026-10-02',
    '2026-10-03',
  ]);
  const RELEASE_TIMESTAMPS = new Set(RELEASE_DATES.map((date) => Date.parse(`${date}T07:00:00+10:00`)));
  const SPOTLIGHT_BOUNDARIES = new Set(Array.from(RELEASE_TIMESTAMPS).flatMap((start) => [start, start + DAY_MS]));
  const PAUSE_NOTICE_START = Date.parse('2026-09-12T07:00:00+10:00');
  const PAUSE_START = Date.parse('2026-09-13T07:00:00+10:00');
  const PAUSE_RESUME = Date.parse('2026-09-14T07:00:00+10:00');
  const isRevealPause = (now) => [PAUSE_START, Date.parse('2026-09-20T07:00:00+10:00'), Date.parse('2026-09-27T07:00:00+10:00')].some((pause) => now >= pause && now < pause + DAY_MS);
  const isPauseNoticeVisible = (now) => [PAUSE_NOTICE_START, Date.parse('2026-09-19T07:00:00+10:00'), Date.parse('2026-09-26T07:00:00+10:00')].some((notice) => now >= notice && now < notice + 2 * DAY_MS);

  const parseCampaignTimestamp = (value) =>
    ['2026-09-07T07:00:00+10:00', '2026-10-05T00:00:00+10:00'].includes(value) ? Date.parse(value) : NaN;

  const parseSpotlightTimestamp = (value) =>
    typeof value === 'string' && /^2026-\d{2}-\d{2}T07:00:00\+10:00$/.test(value)
      && SPOTLIGHT_BOUNDARIES.has(Date.parse(value)) ? Date.parse(value) : NaN;

  const FIRST_RELEASE_TIMESTAMP = Date.parse(`${RELEASE_DATES[0]}T07:00:00+10:00`);
  const isProductReleased = (now, releaseAt, firstRevealEarly = false) =>
    Number.isFinite(now) && RELEASE_TIMESTAMPS.has(releaseAt)
      && (now >= releaseAt || firstRevealEarly && releaseAt === FIRST_RELEASE_TIMESTAMP);

  const classifyCampaignWindow = (now, start, end) => {
    if (![now, start, end].every(Number.isFinite) || end <= start) return 'static';
    return now < start ? 'upcoming' : now < end ? 'live' : 'ended';
  };

  const nextRevealTarget = (now, start, end) => {
    if (classifyCampaignWindow(now, start, end) === 'static') return NaN;
    if (now < start) return start;
    return Array.from(RELEASE_TIMESTAMPS).find((release) => release > now && release < end) ?? NaN;
  };

  const classifySpotlightWindow = (now, start, end) => {
    if (![now, start, end].every(Number.isFinite) || end - start !== DAY_MS) return 'invalid';
    return now < start ? 'upcoming' : now < end ? 'live' : 'ended';
  };

  const splitRemainingTime = (milliseconds) => {
    let remainingSeconds = Math.max(0, Math.ceil(milliseconds / SECOND_MS));
    const days = Math.floor(remainingSeconds / (DAY_MS / SECOND_MS));
    remainingSeconds -= days * (DAY_MS / SECOND_MS);
    const hours = Math.floor(remainingSeconds / (HOUR_MS / SECOND_MS));
    remainingSeconds -= hours * (HOUR_MS / SECOND_MS);
    const minutes = Math.floor(remainingSeconds / (MINUTE_MS / SECOND_MS));
    const seconds = remainingSeconds - minutes * (MINUTE_MS / SECOND_MS);
    return { days, hours, minutes, seconds };
  };

  const splitSpotlightRemainingTime = (milliseconds) => {
    let remainingSeconds = Math.max(0, Math.ceil(milliseconds / SECOND_MS));
    const hours = Math.floor(remainingSeconds / (HOUR_MS / SECOND_MS));
    remainingSeconds -= hours * (HOUR_MS / SECOND_MS);
    const minutes = Math.floor(remainingSeconds / (MINUTE_MS / SECOND_MS));
    const seconds = remainingSeconds - minutes * (MINUTE_MS / SECOND_MS);
    return { hours, minutes, seconds };
  };

  const formatAccessibleRemaining = ({ days, hours, minutes }) => {
    const units = [];
    if (days > 0) units.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    if (hours > 0 || days > 0) units.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    if (minutes > 0 || units.length > 0) units.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    return units.length > 0 ? units.join(', ') : 'less than one minute';
  };

  if (globalThis.__MJS_DDS_ENABLE_TEST_API__ === true) {
    globalThis.__MJS_DDS_TEST_API__ = Object.freeze({
      classifyCampaignWindow,
      classifySpotlightWindow,
      parseCampaignTimestamp,
      parseSpotlightTimestamp,
      splitRemainingTime,
      splitSpotlightRemainingTime,
      isProductReleased,
      nextRevealTarget,
      isRevealPause,
      isPauseNoticeVisible,
    });
  }

  const patchPreviewLink = (link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!link.href || link.hash?.startsWith('#') && link.pathname === window.location.pathname) return;

    const current = new URL(window.location.href);
    const previewThemeId = current.searchParams.get('preview_theme_id');
    if (!previewThemeId) return;

    let destination;
    try {
      destination = new URL(link.href, window.location.origin);
    } catch {
      return;
    }

    if (destination.origin !== window.location.origin) return;
    destination.searchParams.set('preview_theme_id', previewThemeId);
    link.href = destination.toString();
  };

  const synchroniseScheduledSpotlight = (root, now, campaignState) => {
    const region = root.querySelector('[data-mjs-dds-spotlight]');
    const fallback = region?.querySelector('[data-mjs-dds-spotlight-fallback]');
    const fallbackKicker = fallback?.querySelector('[data-mjs-dds-spotlight-fallback-kicker]');
    const fallbackHeading = fallback?.querySelector('[data-mjs-dds-spotlight-fallback-heading]');
    const fallbackText = fallback?.querySelector('[data-mjs-dds-spotlight-fallback-text]');
    const stage = region?.querySelector('[data-mjs-dds-spotlight-stage]');
    if (!region || !fallback || !fallbackKicker || !fallbackHeading || !fallbackText || !stage) return;

    const templates = Array.from(region.querySelectorAll('[data-mjs-dds-spotlight-template]'));

    const setFallback = (state, kicker, heading, text) => {
      if (region.dataset.mjsDdsSpotlightState === state && !stage.hasChildNodes()) return;
      stage.replaceChildren();
      stage.hidden = true;
      fallback.hidden = false;
      fallbackKicker.textContent = kicker;
      fallbackHeading.textContent = heading;
      fallbackText.textContent = text;
      region.dataset.mjsDdsSpotlightState = state;
      delete region.dataset.mjsDdsActiveDropDate;
      delete region.dataset.mjsDdsActiveDropNumber;
    };

    const activateTemplate = (template, state) => {
      if (!(template instanceof HTMLTemplateElement)) return false;
      const activeDropNumber = template.dataset.dropNumber;
      if (
        region.dataset.mjsDdsSpotlightState !== state
        || region.dataset.mjsDdsActiveDropNumber !== activeDropNumber
        || !stage.hasChildNodes()
      ) {
        stage.replaceChildren(template.content.cloneNode(true));
        stage.querySelectorAll('a[href]').forEach(patchPreviewLink);
        stage.hidden = false;
        fallback.hidden = true;
        region.dataset.mjsDdsSpotlightState = state;
        region.dataset.mjsDdsActiveDropNumber = activeDropNumber;
      }
      return true;
    };

    if (campaignState === 'upcoming') {
      const firstTemplate = templates.find((candidate) => candidate.dataset.dropNumber === '01');
      if (activateTemplate(firstTemplate, 'upcoming')) {
        const heading = stage.querySelector('[data-mjs-dds-feature-heading]');
        const timer = stage.querySelector('[data-mjs-dds-feature-countdown]');
        if (heading) heading.textContent = root.dataset.firstRevealEarly === 'true' ? 'Live Now' : 'A first look';
        if (timer) timer.hidden = true;
        return;
      }
      setFallback(
        'upcoming',
        'Daily reveals at 7:00 am AEST',
        'One diamond takes the spotlight each day.',
        'The collection begins on 7 September. Each new reveal will appear here.',
      );
      return;
    }
    if (campaignState === 'ended' || now >= Math.max(...RELEASE_TIMESTAMPS) + DAY_MS) {
      setFallback(
        'ended',
        'The Diamond Drop',
        'All drops revealed—explore the collection.',
        'Shop the revealed pieces below. Their Drop prices remain until sold.',
      );
      return;
    }
    if (campaignState !== 'live') {
      setFallback(
        'static',
        'The Diamond Drop',
        'Explore the revealed collection.',
        'Each product page confirms current details, price and availability.',
      );
      return;
    }

    if (isRevealPause(now)) {
      setFallback(
        'paused',
        'The Diamond Drop',
        'New reveals resume on Monday.',
        'Our next piece arrives Monday at 7:00 am AEST. Explore the previously revealed pieces below, available at their Drop price until sold.',
      );
      return;
    }
    const template = templates.find((candidate) => {
      const start = parseSpotlightTimestamp(candidate.dataset.dropStart);
      const end = parseSpotlightTimestamp(candidate.dataset.dropEnd);
      return classifySpotlightWindow(now, start, end) === 'live';
    });
    if (!(template instanceof HTMLTemplateElement)) {
      setFallback(
        'unavailable',
        'Today’s Diamond Drop',
        'Today’s featured piece is currently unavailable.',
        'Explore the previously revealed pieces below.',
      );
      return;
    }
    activateTemplate(template, 'live');
  };

  const initialiseCampaignCountdown = (root, signal, cleanupTasks) => {
    const region = root.querySelector('[data-mjs-dds-countdown]');
    const label = region?.querySelector('[data-mjs-dds-countdown-label]');
    const clock = region?.querySelector('[data-mjs-dds-countdown-clock]');
    const readable = region?.querySelector('[data-mjs-dds-countdown-readable]');
    const announcement = region?.querySelector('[data-mjs-dds-countdown-announcement]');
    const valueElements = Object.fromEntries(['days', 'hours', 'minutes', 'seconds'].map(
      (unit) => [unit, region?.querySelector(`[data-mjs-dds-countdown-${unit}]`)],
    ));
    if (
      !region || !label || !clock || !readable || !announcement
      || Object.values(valueElements).some((element) => !element)
    ) return;

    const start = parseCampaignTimestamp(region.dataset.campaignStart);
    const end = parseCampaignTimestamp(region.dataset.campaignEnd);
    let timeoutId = null;
    let previousState = null;
    let previousMinuteBucket = null;

    const clearScheduledRender = () => {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      timeoutId = null;
    };

    const render = () => {
      clearScheduledRender();
      const now = Date.now();
      const state = classifyCampaignWindow(now, start, end);
      const scheduleNote = root.querySelector('[data-mjs-dds-schedule-note]');
      if (scheduleNote) scheduleNote.hidden = !isPauseNoticeVisible(now);
      root.dispatchEvent(new CustomEvent('mjs:release-clock', { detail: { now } }));
      synchroniseScheduledSpotlight(root, now, state);
      region.dataset.mjsDdsCountdownState = state;

      if (state === 'static') {
        label.hidden = false;
        label.textContent = 'Daily reveals at 7:00 am AEST';
        clock.hidden = true;
        readable.textContent = 'From 7 September, a new piece is revealed daily at 7:00 am AEST.';
        return;
      }
      if (state === 'ended') {
        label.hidden = false;
        label.textContent = 'All scheduled pieces have been revealed.';
        clock.hidden = true;
        readable.textContent = 'Explore the revealed collection. The Drop price remains until sold.';
        if (previousState === 'live') announcement.textContent = 'All scheduled pieces have been revealed.';
        previousState = state;
        return;
      }

      const target = nextRevealTarget(now, start, end);
      if (!Number.isFinite(target)) {
        label.hidden = false;
        label.textContent = 'All scheduled pieces have been revealed.';
        clock.hidden = true;
        readable.textContent = 'Explore the revealed collection. The Drop price remains until sold.';
        return;
      }
      const remaining = Math.max(0, target - now);
      const values = state === 'upcoming' ? splitRemainingTime(remaining) : splitSpotlightRemainingTime(remaining);
      label.hidden = false;
      label.textContent = state === 'upcoming' ? 'First daily reveal in'
        : 'Next reveal in';
      clock.hidden = false;
      Object.entries(values).forEach(([unit, value]) => {
        valueElements[unit].textContent = String(value).padStart(2, '0');
      });

      const minuteBucket = Math.floor(remaining / MINUTE_MS);
      if (state !== previousState || minuteBucket !== previousMinuteBucket) {
        readable.textContent = `${label.textContent} ${formatAccessibleRemaining(values)}. Daily reveals at 7:00 am AEST. The Drop price remains until sold.`;
        previousMinuteBucket = minuteBucket;
      }
      if (previousState === 'upcoming' && state === 'live') {
        announcement.textContent = 'The sale is now live.';
      }
      previousState = state;

      if (!document.hidden) {
        const delay = Math.max(100, SECOND_MS - (Date.now() % SECOND_MS) + 20);
        timeoutId = window.setTimeout(render, delay);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) clearScheduledRender();
      else render();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange, { signal });
    window.addEventListener('pageshow', render, { signal });
    cleanupTasks.push(clearScheduledRender);
    render();
  };

  const initialiseDocumentationMarquee = (root, signal, cleanupTasks) => {
    const region = root.querySelector('[data-mjs-dds-documentation]');
    const marquee = region?.querySelector('[data-mjs-dds-documentation-marquee]');
    const track = region?.querySelector('[data-mjs-dds-documentation-track]');
    const primaryGroup = region?.querySelector('[data-mjs-dds-documentation-group]:not([aria-hidden="true"])');
    const status = region?.querySelector('[data-mjs-dds-marquee-status]');
    if (!region || !marquee || !track || !primaryGroup || !status) return;

    track.querySelectorAll('[data-mjs-dds-documentation-clone]').forEach((clone) => clone.remove());

    const clones = [1, 2].map(() => {
      const clone = primaryGroup.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('role', 'presentation');
      clone.setAttribute('data-mjs-dds-documentation-clone', '');
      clone.removeAttribute('data-mjs-dds-documentation-group');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('img').forEach((image) => image.setAttribute('alt', ''));
      clone.querySelectorAll('[tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
      track.appendChild(clone);
      return clone;
    });

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let pointerPaused = false;
    let focusPaused = false;
    let touchPaused = false;
    let offscreenPaused = false;
    let focusFromPointer = false;
    let touchPointer = null;

    const sync = () => {
      const isStatic = reducedMotion.matches;
      const isPaused = pointerPaused || focusPaused || touchPaused || offscreenPaused;
      region.dataset.mjsDdsMarqueeState = isStatic ? 'static' : isPaused ? 'paused' : 'running';
      marquee.tabIndex = isStatic ? -1 : 0;
    };

    marquee.addEventListener('mouseenter', () => { pointerPaused = true; sync(); }, { signal });
    marquee.addEventListener('mouseleave', () => { pointerPaused = false; sync(); }, { signal });
    marquee.addEventListener('focusin', () => {
      focusPaused = !focusFromPointer;
      sync();
    }, { signal });
    marquee.addEventListener('focusout', () => {
      window.queueMicrotask(() => {
        if (!marquee.isConnected) return;
        focusPaused = marquee.contains(document.activeElement) && !focusFromPointer;
        sync();
      });
    }, { signal });

    marquee.addEventListener('pointerdown', (event) => {
      focusFromPointer = true;
      if (event.pointerType === 'touch' || event.pointerType === 'pen') {
        touchPointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          moved: false,
        };
      }
    }, { signal });
    marquee.addEventListener('pointermove', (event) => {
      if (!touchPointer || touchPointer.id !== event.pointerId) return;
      const distance = Math.hypot(event.clientX - touchPointer.x, event.clientY - touchPointer.y);
      if (distance > 10) touchPointer.moved = true;
    }, { signal });
    marquee.addEventListener('pointerup', (event) => {
      if (touchPointer?.id === event.pointerId && !touchPointer.moved) {
        touchPaused = !touchPaused;
        status.textContent = touchPaused
          ? 'Credential logo movement paused. Tap again to resume.'
          : 'Credential logo movement resumed.';
        sync();
      }
      touchPointer = null;
      window.setTimeout(() => { focusFromPointer = false; }, 0);
    }, { signal });
    marquee.addEventListener('pointercancel', () => {
      touchPointer = null;
      window.setTimeout(() => { focusFromPointer = false; }, 0);
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (
        touchPaused &&
        (event.pointerType === 'touch' || event.pointerType === 'pen') &&
        !marquee.contains(event.target)
      ) {
        touchPaused = false;
        status.textContent = 'Credential logo movement resumed.';
        sync();
      }
    }, { signal });

    const handleMotionChange = () => sync();
    reducedMotion.addEventListener?.('change', handleMotionChange);
    cleanupTasks.push(() => reducedMotion.removeEventListener?.('change', handleMotionChange));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          offscreenPaused = !entries[0]?.isIntersecting;
          sync();
        },
        { threshold: 0.01 }
      );
      observer.observe(region);
      cleanupTasks.push(() => observer.disconnect());
    }

    cleanupTasks.push(() => clones.forEach((clone) => clone.remove()));
    sync();
  };

  const initialiseCatalogue = (root, signal) => {
    const catalogue = root.querySelector('[data-mjs-dds-catalogue]');
    const grid = catalogue?.querySelector('[data-dds-grid]');
    const filterButtons = Array.from(catalogue?.querySelectorAll('[data-dds-filter]') || []);
    const sortSelect = catalogue?.querySelector('[data-dds-sort]');
    const count = catalogue?.querySelector('[data-dds-count]');
    const empty = catalogue?.querySelector('[data-dds-empty]');
    const tools = catalogue?.querySelector('.mjs-dds__catalogue-tools');
    const items = Array.from(catalogue?.querySelectorAll('[data-dds-item]') || []);
    if (!catalogue || !grid || !sortSelect || !count || !empty || items.length === 0) return;

    const allowedCategories = new Set(filterButtons.map((button) => button.dataset.ddsFilter));
    const allowedSorts = new Set(['price-descending', 'price-ascending', 'featured', 'title-ascending']);

    const readState = () => {
      const url = new URL(window.location.href);
      const requestedCategory = url.searchParams.get('dds_category') || 'all';
      const requestedSort = url.searchParams.get('dds_sort') || 'featured';
      return {
        category: allowedCategories.has(requestedCategory) ? requestedCategory : 'all',
        sort: allowedSorts.has(requestedSort) ? requestedSort : 'featured',
      };
    };

    const updateUrl = (state) => {
      const url = new URL(window.location.href);
      if (state.category === 'all') url.searchParams.delete('dds_category');
      else url.searchParams.set('dds_category', state.category);
      if (state.sort === 'featured') url.searchParams.delete('dds_sort');
      else url.searchParams.set('dds_sort', state.sort);
      window.history.replaceState({}, '', url);
    };

    const sortItems = (sort) => {
      return [...items].sort((left, right) => {
        if (sort === 'price-ascending') return Number(left.dataset.price) - Number(right.dataset.price);
        if (sort === 'price-descending') return Number(right.dataset.price) - Number(left.dataset.price);
        if (sort === 'title-ascending') return left.dataset.title.localeCompare(right.dataset.title);
        return Number(right.dataset.featuredOrder) - Number(left.dataset.featuredOrder);
      });
    };

    let releaseKey = '';
    const firstRevealEarly = root.dataset.firstRevealEarly === 'true';
    const releasedItems = (now) => items.filter((item) => isProductReleased(now, Number(item.dataset.releaseAt), firstRevealEarly));
    const applyState = (state, shouldUpdateUrl = true, now = Date.now()) => {
      const released = releasedItems(now);
      const releasedSet = new Set(released);
      releaseKey = released.map((item) => item.dataset.releaseAt).join('|');
      if (tools) tools.hidden = released.length < 2;
      filterButtons.forEach((button) => {
        button.hidden = button.dataset.ddsFilter !== 'all'
          && !released.some((item) => item.dataset.category === button.dataset.ddsFilter);
        const active = button.dataset.ddsFilter === state.category;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      sortSelect.value = state.sort;

      const ordered = sortItems(state.sort);
      ordered.forEach((item) => grid.appendChild(item));

      let visibleCount = 0;
      items.forEach((item) => {
        const visible = releasedSet.has(item)
          && (state.category === 'all' || item.dataset.category === state.category);
        item.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      count.textContent = `${visibleCount} revealed ${visibleCount === 1 ? 'piece' : 'pieces'}`;
      empty.textContent = released.length === 0
        ? 'The collection begins on 7 September. Each new reveal will appear here.'
        : 'No revealed pieces match this filter.';
      empty.hidden = visibleCount !== 0;
      if (shouldUpdateUrl) updateUrl(state);
    };

    filterButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => applyState({ category: button.dataset.ddsFilter, sort: sortSelect.value }),
        { signal }
      );
    });
    sortSelect.addEventListener(
      'change',
      () => applyState({ category: readState().category, sort: sortSelect.value }),
      { signal }
    );
    window.addEventListener('popstate', () => applyState(readState(), false), { signal });
    root.addEventListener('mjs:release-clock', (event) => {
      const nextKey = releasedItems(event.detail.now).map((item) => item.dataset.releaseAt).join('|');
      if (nextKey !== releaseKey) applyState(readState(), false, event.detail.now);
    }, { signal });
    applyState(readState(), false);
  };

  const initialiseRoot = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.mjsDdsInitialised === 'true') return;
    root.dataset.mjsDdsInitialised = 'true';

    const controller = new AbortController();
    const cleanupTasks = [];
    root.querySelectorAll('a[href]').forEach(patchPreviewLink);
    initialiseCatalogue(root, controller.signal);
    initialiseCampaignCountdown(root, controller.signal, cleanupTasks);
    initialiseDocumentationMarquee(root, controller.signal, cleanupTasks);

    const quickViewOverlay = document.getElementById('mjs-qv-overlay');
    if (quickViewOverlay) {
      const patchQuickViewLinks = () => quickViewOverlay.querySelectorAll('a[href]').forEach(patchPreviewLink);
      const quickViewObserver = new MutationObserver(patchQuickViewLinks);
      quickViewObserver.observe(quickViewOverlay, {
        attributes: true,
        attributeFilter: ['href'],
        childList: true,
        subtree: true,
      });
      patchQuickViewLinks();
      cleanupTasks.push(() => quickViewObserver.disconnect());
    }

    rootCleanups.set(root, () => {
      controller.abort();
      cleanupTasks.forEach((cleanup) => cleanup());
      root.removeAttribute('data-mjs-dds-initialised');
      rootCleanups.delete(root);
    });
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialiseRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialiseRoot);
  };

  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);
    rootCleanups.get(root)?.();
  });
})();
